/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect, memo, useRef } from 'react';
import _ from 'lodash';
import { Navigate, useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { store } from 'redux/store';

import PageContainer from '@components/page-container';
import PageHeader from '@components/page-header';
import SiteDetailsMoreInfo from './site-details-more-info';
import SiteBookmark from '@components/site-listing/site-bookmark';
import EquipmentListing from '@components/equipment-listing';
import StructureListing from '@components/structure-listing';
import PipelineListing from '@components/pipeline-listing';
import LoadingRound from '@components/loading-round/loading-round';
import NewTabs from '@components/new-tabs';
import SiteCard from '@components/site-card';
import WorkOrder from './tasks/work-order';
import Tasks from '@components/site-parent-site/tasks';
import { moreInfoDropdownList, tabsItemMap } from '@components/site-parent-site/helper';
import BookmarkErrorScreen from '@components/bookmark-error-screen';

import useWindowDimensions from '@hooks/useWindowDimensions';
import { log } from '@utilities/logger.util';
import { getAllSiteDetailsData } from './helper';
import { backHistoryActions } from '@redux/slices/back-history';
import { getAppDispatch } from '@utilities/dispatch.util';
import { filterActions } from '@redux/slices/filters';
import { initWebWorker } from 'webworker';

import style from './style.module.scss';
import './assets-details.scss';
import MapListing from '@components/map-listing';
import { siteMapActions } from '@redux/slices/site-map';
import SiteCardMenuBottom from '@components/site-card-menu-bottom';
import useNetworkStatus from '@hooks/useNetworkStatus';
import DeleteModal from '@components/modal/variants/delete-modal';
import { useToastContext } from '@context/toaster.context';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';

const AssetsDetails = () => {
  const { width } = useWindowDimensions();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = getAppDispatch();
  const navigate = useNavigate();
  const [isOnline] = useNetworkStatus();
  const { ToasterUtil } = useToastContext();

  const { showMapView = false, selectedSiteIdForMapView } =
    useSelector((state) => state.siteMap) || {};

  const { id } = useParams();
  const bookmarkName = searchParams.get('bookmarkName');
  const bookmarkId = searchParams.get('bookmarkId');
  const isBookmarkAtAGlance = searchParams.get('isBookmarkAtAGlance') || false;
  const bookmarkTab = searchParams.get('bookmarkTab');
  const currentOpenTaskTab = searchParams.get('currentOpenTaskTab') || '';
  const templateString = searchParams.get('siteTemplates') || '';
  const selectedTaskType = searchParams.get('selectedTaskType');

  const swipeAbleMenuRef = useRef(null);
  const mainDivRef = useRef(null);
  const siteCardRef = useRef(null);
  const assetDetailPageHeaderRef = useRef(null);

  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('0');
  const [moreInfo, setMoreInfo] = useState(false);
  const [isFilterActive, setFilterActive] = useState(width < 768 ? false : true);
  const [deleteBookmark, setDeleteBookmark] = useState(false);

  const [isFilterActiveForEquipments, setFilterActiveForEquipments] = useState(
    width < 768 ? false : true
  );
  const [isFilterActiveForPipeline, setFilterActiveForPipeline] = useState(
    width < 768 ? false : true
  );
  const [isFilterActiveForStructure, setFilterActiveForStructure] = useState(
    width < 768 ? false : true
  );
  const [allTask, setAllTask] = useState(false);
  const [checks, setChecks] = useState(false);
  const [inspections, setInspections] = useState(false);
  const [workOrder, setWorkOrder] = useState(false);
  const [workRequest, setWorkRequest] = useState(false);
  const [newOrEditAssets, setNewOrEditAssets] = useState(false);
  const [hseEvent, setHseEvent] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [assetDetailsData, setAssetDetailsData] = useState({});

  useEffect(() => {
    if (currentOpenTaskTab) {
      if (currentOpenTaskTab === 'allTask') setAllTask(true);
      else if (currentOpenTaskTab === 'workOrder') setWorkOrder(true);
      else if (currentOpenTaskTab === 'inspections') setInspections(true);
      else if (currentOpenTaskTab === 'workRequest') setWorkRequest(true);
      else if (currentOpenTaskTab === 'newOrEditAssets') setNewOrEditAssets(true);
      else if (currentOpenTaskTab === 'hseEvent') setHseEvent(true);
      else if (currentOpenTaskTab === 'checks') setChecks(true);
    }
  }, [currentOpenTaskTab]);

  const siteDetailsPage = useSelector((state) => state.backHistory.siteDetailsPage);
  const { picklistsMap = {} } = useSelector((state) => state.picklists);
  const {
    tasksFilterCount = 0,
    structureFilterCount = 0,
    equipmentFilterCount = 0,
    pipelineFilterCount = 0
  } = useSelector((state) => state.filters);
  const { tasksList: reduxTasksList = [], selectTask } = useSelector((state) => state.tasks);
  const { inspectionsList } = useSelector((state) => state.inspection);
  const { HSEEventList } = useSelector((state) => state.hseEvents);
  const { checksList } = useSelector((state) => state.checks);
  const { syncedNewAndEditedEquipments = [] } = useSelector((state) => state.cache);
  const { utFormDownloadingLoading } = useSelector((state) => state.utFormData) || {};

  useEffect(() => {
    if (!_.isEmpty(siteDetailsPage)) {
      siteDetailsPage?.siteDetailsTab && setTab(siteDetailsPage?.siteDetailsTab || '0');
      siteDetailsPage?.workOrder && setWorkOrder(siteDetailsPage?.workOrder);
      siteDetailsPage?.workRequest && setWorkRequest(siteDetailsPage?.workRequest);
      siteDetailsPage?.newOrEditAssets && setNewOrEditAssets(siteDetailsPage?.newOrEditAssets);
    }
  }, [siteDetailsPage]);

  const templatesIds = useMemo(
    () => templateString.split(',').filter((el) => el),
    [templateString]
  );

  useEffect(() => {
    if (utFormDownloadingLoading) {
      return;
    }

    initWebWorker({
      args: {
        store: { ...store.getState() },
        siteId: id,
        templatesIds,
        selectedTaskType
      },
      type: 'get-site-details'
    })
      .then((data) => {
        setAssetDetailsData({ ...data?.result, siteId: id });
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, [
    id,
    templatesIds,
    reduxTasksList,
    inspectionsList,
    HSEEventList,
    checksList,
    syncedNewAndEditedEquipments,
    utFormDownloadingLoading
  ]);

  useEffect(() => {
    if (syncComplete) setSyncComplete(false);
  }, [syncComplete]);

  const {
    sitesLoading,
    name,
    tasks = [],
    allDoneTasks,
    allWorkOrders,
    allDoneWorkOrders,
    allInspections,
    allChecks,
    allEditEquipmentAssets,
    allDoneChecks,
    allDoneInspections,
    isInspectionOverDue,
    isWorkOrderOverDue,
    isCheckOverDue,
    isOverDue,
    status,
    area,
    equipments = [],
    dataPoints,
    unit,
    distance,
    children,
    parentSite,
    MoreInfoDataPointsMap,
    equipmentAllFieldData,
    structures,
    structureAllFieldData,
    pipelines = [],
    equipmentCount,
    structureCount,
    pipelineCount,
    link,
    newWorkRequestList = [],
    hseEvents = [],
    coordinatesList,
    onDemandRecordList = 0
  } = assetDetailsData;

  useMemo(() => {
    if (!isLoading) {
      if (!sitesLoading && !parentSite?.siteId) return <Navigate to={'/sites'} />;
      !dataPoints && log(unit, distance, children);
    }
  }, [assetDetailsData, isLoading]);

  const onParentClickEvent = ({ workOrderTab, workOrderSortTab }) => {
    dispatch?.(
      backHistoryActions.setSiteDetailsPageHistory({
        workOrder,
        workRequest,
        newOrEditAssets,
        siteDetailsTab: tab,
        workOrderTab,
        workOrderSortTab
      })
    );
  };

  const arrayText = useMemo(() => {
    return moreInfoDropdownList({ setMoreInfo, setOpen, setOpenModal, link, width });
  }, [link, width]);

  const itemsMap = useMemo(() => {
    return tabsItemMap({
      isLoading,
      tasks,
      children: [],
      equipments,
      structures,
      pipelines,
      onDemandRecordList,
      allEditEquipmentAssets
    });
  }, [isLoading, onDemandRecordList, allEditEquipmentAssets]);

  const handleInfoClick = () => {
    const infoTabIndex = items?.findIndex((x) => x?.label === 'Info');
    setTab(infoTabIndex);
  };

  const handleBookmarkClick = () => {
    setOpenModal(true);
  };

  const SiteCardCOmponent = ({
    moreInfoTabActive,
    showCard = false,
    className = '',
    isInfo = false
  }) =>
    showCard ? (
      <div className={`marginClass ${className}`} ref={siteCardRef}>
        <SiteCard
          siteDetailsCard
          chips={false}
          title={area}
          subtitle={name}
          hideLocation={true}
          dataPoints={dataPoints || []}
          allTasksForSite={tasks}
          allDoneTasks={allDoneTasks}
          isOverDue={isOverDue}
          status={status}
          arrayText={arrayText}
          open={open}
          setOpen={setOpen}
          moreInfo={moreInfo}
          equipmentCount={equipmentCount}
          structureCount={structureCount}
          pipelineCount={pipelineCount}
          allOnDemandRecordCount={onDemandRecordList + (allEditEquipmentAssets?.length || 0)}
          onInfoCLick={handleInfoClick}
          onBookmarkCLick={handleBookmarkClick}
          infoTabActive={moreInfoTabActive}
          toDesktopLink={link && width >= 768 ? link : undefined}
          showInfoIcon={
            MoreInfoDataPointsMap &&
            !(allTask || checks || workOrder || workRequest || inspections || newOrEditAssets)
          }
          className={`${isInfo && style.borderBottomNone}`}
        />
      </div>
    ) : (
      <></>
    );

  const items = useMemo(() => {
    return !isLoading
      ? [
          ...(tasks?.length > 0 || onDemandRecordList > 0 || allEditEquipmentAssets?.length > 0
            ? [
                {
                  label: `Tasks + records (${
                    tasks.length + onDemandRecordList + allEditEquipmentAssets?.length
                  })`,
                  keyName: 'tasks',
                  children: (
                    <>
                      <Tasks
                        setChecks={setChecks}
                        setAllTask={setAllTask}
                        setWorkOrder={setWorkOrder}
                        setInspections={setInspections}
                        setWorkRequest={setWorkRequest}
                        setNewOrEditAssets={setNewOrEditAssets}
                        setHseEvent={setHseEvent}
                        allWorkOrderForSite={allWorkOrders}
                        allDoneWorkOrder={allDoneWorkOrders}
                        allInspectionForSite={allInspections}
                        allDoneInspection={allDoneInspections}
                        allCheckForSite={allChecks}
                        allEditEquipmentAssets={allEditEquipmentAssets}
                        allDoneChecks={allDoneChecks}
                        allTasksForSite={tasks}
                        allDoneTasks={allDoneTasks}
                        isOverDue={isOverDue}
                        isInspectionOverDue={isInspectionOverDue}
                        isCheckOverDue={isCheckOverDue}
                        isWorkOrderOverDue={isWorkOrderOverDue}
                        newWorkRequestList={newWorkRequestList}
                        hseEvents={hseEvents}
                        onDemandRecordList={onDemandRecordList + allEditEquipmentAssets?.length}
                        newWorkOrderList={assetDetailsData?.newWorkOrderList || []}
                        newInspectionList={assetDetailsData?.newInspectionList || []}
                        newChecksList={assetDetailsData?.newChecksList || []}
                        card={() => (
                          <SiteCardCOmponent
                            moreInfoTabActive={false}
                            showCard={width <= 768 ? true : false}
                            className={`mb16 ${style.pt0}`}
                          />
                        )}
                      />
                    </>
                  )
                }
              ]
            : []),

          ...(structures?.length > 0
            ? [
                {
                  label: `Structure (${structures?.length})`,
                  keyName: 'structure',

                  children: (
                    <>
                      <StructureListing
                        assetDetailsStructureLists={structureAllFieldData}
                        isFilterActiveHeader={isFilterActiveForStructure}
                        setFilterActiveHeader={setFilterActiveForStructure}
                        from={location.pathname}
                        siteId={id}
                        detailPage
                        noSite
                        detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'structure'}
                        card={() => (
                          <SiteCardCOmponent
                            moreInfoTabActive={false}
                            className={` ${style.pt0}`}
                            showCard={
                              !(
                                tasks?.length > 0 ||
                                onDemandRecordList > 0 ||
                                allEditEquipmentAssets?.length > 0
                              ) && width <= 768
                                ? true
                                : false
                            }
                          />
                        )}
                      />
                    </>
                  )
                }
              ]
            : []),

          ...(equipments?.length > 0
            ? [
                {
                  label: `Equipment (${equipments.length})`,
                  keyName: 'equipment',
                  children: (
                    <>
                      <EquipmentListing
                        assetDetailsOfEquipmentLists={equipmentAllFieldData}
                        isFilterActiveHeader={isFilterActiveForEquipments}
                        setFilterActiveHeader={setFilterActiveForEquipments}
                        from={location.pathname}
                        siteId={id}
                        detailPage
                        detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'equipment'}
                        noSite
                        noDistanceTab
                        card={() => (
                          <SiteCardCOmponent
                            moreInfoTabActive={false}
                            className={`${style.pt0}`}
                            showCard={
                              !(
                                tasks?.length > 0 ||
                                onDemandRecordList > 0 ||
                                allEditEquipmentAssets?.length > 0 ||
                                structures?.length > 0
                              ) && width <= 768
                                ? true
                                : false
                            }
                          />
                        )}
                      />
                    </>
                  )
                }
              ]
            : []),

          ...(pipelines?.length > 0
            ? [
                {
                  label: `Pipeline (${pipelines?.length})`,
                  keyName: 'pipeline',
                  children: (
                    <>
                      <PipelineListing
                        assetDetailsPipelineLists={pipelines}
                        isFilterActiveHeader={isFilterActiveForPipeline}
                        setFilterActiveHeader={setFilterActiveForPipeline}
                        from={location.pathname}
                        siteId={id}
                        detailPage
                        detailsPageBookmarks={searchParams.get('bookmarkKeyName') === 'pipeline'}
                        card={() => (
                          <SiteCardCOmponent
                            moreInfoTabActive={false}
                            className={` ${style.pt0}`}
                            showCard={
                              !(
                                tasks?.length > 0 ||
                                onDemandRecordList > 0 ||
                                allEditEquipmentAssets?.length > 0 ||
                                structures?.length > 0 ||
                                equipments?.length > 0
                              ) && width <= 768
                                ? true
                                : false
                            }
                          />
                        )}
                      />
                    </>
                  )
                }
              ]
            : []),

          ...(MoreInfoDataPointsMap
            ? [
                {
                  label: `Info`,
                  keyName: 'info',
                  children: (
                    <>
                      <div className={style.infoTab} id="site-info-tab">
                        <SiteCardCOmponent
                          moreInfoTabActive={true}
                          showCard={width <= 768 ? true : false}
                          className={`${style.pt0}`}
                          isInfo
                        />
                        <SiteDetailsMoreInfo moreInfoDataPointsMap={MoreInfoDataPointsMap} />
                      </div>
                    </>
                  )
                }
              ]
            : []),

          ...(coordinatesList?.allSiteCoordinatesList?.length > 0 && isOnline
            ? [
                {
                  label: `Map`,
                  keyName: 'map',
                  children: (
                    <>
                      <MapListing
                        detailPage={true}
                        siteData={[assetDetailsData]}
                        allSiteCoordinatesList={coordinatesList.allSiteCoordinatesList}
                        siteChildrenList={[assetDetailsData]}
                      />
                    </>
                  )
                }
              ]
            : [])
        ]
      : [];
  }, [
    isLoading,
    itemsMap,
    isFilterActive,
    isFilterActiveForEquipments,
    isFilterActiveForPipeline,
    isFilterActiveForStructure,
    allDoneTasks,
    tasks,
    allInspections,
    hseEvents,
    structureAllFieldData,
    distance,
    assetDetailsData,
    newWorkRequestList,
    onDemandRecordList,
    structures,
    isOnline,
    allEditEquipmentAssets,
    width
  ]);

  useEffect(() => {
    if (items?.length > 0) {
      const params = new URLSearchParams(location.search);
      const currentTab = parseInt(tab);
      params.set('tab', currentTab);
      if (items[currentTab]?.keyName === 'tasks') {
        if (allTask) params.set('currentOpenTaskTab', 'allTask');
        else if (workOrder) params.set('currentOpenTaskTab', 'workOrder');
        else if (inspections) params.set('currentOpenTaskTab', 'inspections');
        else if (workRequest) params.set('currentOpenTaskTab', 'workRequest');
        else if (hseEvent) params.set('currentOpenTaskTab', 'hseEvent');
        else if (checks) params.set('currentOpenTaskTab', 'checks');
        else if (newOrEditAssets) params.set('currentOpenTaskTab', 'newOrEditAssets');
        else if (
          !workOrder &&
          !allTask &&
          !inspections &&
          !workRequest &&
          !hseEvent &&
          !checks &&
          !newOrEditAssets
        )
          params.delete('currentOpenTaskTab');
      }
      params.set(
        'virtualScrollTab',
        currentTab === 0 && items[currentTab]?.keyName === 'tasks'
          ? -1
          : tasks?.length > 0
          ? currentTab - 1
          : currentTab
      );
      params.set('keyName', items[currentTab]?.keyName);
      const tabParams = '?' + params.toString();
      const prevRoute = `${location.pathname}${tabParams}`;
      navigate(prevRoute, { replace: true });
    }
  }, [tab, items, workOrder, allTask, inspections, workRequest, hseEvent, checks, newOrEditAssets]);

  useEffect(() => {
    if (!isLoading && bookmarkTab) setTab(bookmarkTab);
  }, [isLoading, bookmarkTab]);

  const pageHeaderClickEvent = () => {
    if (selectedTaskType) {
      navigate(-1);
    } else {
      (workOrder || workRequest) && dispatch?.(filterActions.setTaskFilters(null));
      if (moreInfo) setMoreInfo(false);
      else if (allTask) setAllTask(false);
      else if (inspections) setInspections(false);
      else if (workOrder) setWorkOrder(false);
      else if (workRequest) setWorkRequest(false);
      else if (newOrEditAssets) setNewOrEditAssets(false);
      else if (hseEvent) setHseEvent(false);
      else if (checks) setChecks(false);

      dispatch?.(backHistoryActions.setSiteDetailsPageHistory(null));
    }
  };

  const pageHeaderTitle = useMemo(() => {
    return `${
      bookmarkName
        ? bookmarkName
        : moreInfo
        ? 'Site Information'
        : allTask
        ? `Tasks, records (${
            tasks?.length > 0 || onDemandRecordList || allEditEquipmentAssets?.length > 0
              ? tasks?.length + onDemandRecordList + allEditEquipmentAssets?.length
              : ''
          })`
        : checks
        ? `Checks (${allChecks?.length > 0 ? allChecks?.length : ''})`
        : inspections
        ? `Inspections (${allInspections?.length > 0 ? allInspections?.length : ''})`
        : workOrder
        ? `Work orders (${
            allWorkOrders?.length > 0 || assetDetailsData?.newWorkOrderList?.length
              ? allWorkOrders?.length + assetDetailsData?.newWorkOrderList?.length
              : ''
          })`
        : workRequest
        ? `Work requests (${newWorkRequestList?.length > 0 ? newWorkRequestList?.length : ''})`
        : newOrEditAssets
        ? `New or edited assets (${
            allEditEquipmentAssets?.length > 0 ? allEditEquipmentAssets?.length : ''
          })`
        : hseEvent
        ? `HSE events (${hseEvents?.length > 0 ? hseEvents?.length : ''})`
        : 'Site'
    }`;
  }, [
    bookmarkName,
    moreInfo,
    allTask,
    checks,
    inspections,
    workOrder,
    tasks,
    workRequest,
    hseEvent,
    hseEvents,
    newWorkRequestList,
    allEditEquipmentAssets,
    newOrEditAssets,
    assetDetailsData
  ]);

  const isFilterIconIsRequired = useMemo(() => {
    return (!items[tab]?.keyName?.toLowerCase()?.includes('tasks') &&
      !items[tab]?.keyName?.toLowerCase()?.includes('map') &&
      !items[tab]?.keyName?.toLowerCase()?.includes('info')) ||
      allTask ||
      checks ||
      inspections ||
      workOrder ||
      workRequest ||
      hseEvent ||
      newOrEditAssets
      ? items[tab]?.keyName === 'equipment'
        ? setFilterActiveForEquipments
        : items[tab]?.keyName === 'structure'
        ? setFilterActiveForStructure
        : items[tab]?.keyName === 'pipeline'
        ? setFilterActiveForPipeline
        : setFilterActive
      : false;
  }, [tab, items, allTask, checks, inspections, workOrder, workRequest, hseEvent, newOrEditAssets]);

  const siteDetailsPageClassNames = useMemo(() => {
    return `${moreInfo || selectTask ? style.pageListingsWithMoreInfo : style.pageListings} ${
      style.noScroll
    } ${
      (workOrder ||
        allTask ||
        checks ||
        inspections ||
        workRequest ||
        hseEvent ||
        newOrEditAssets) &&
      style.noScroll
    }`;
  }, [workOrder, allTask, checks, inspections, workRequest, selectTask, hseEvent, newOrEditAssets]);

  const filtersCount = useMemo(() => {
    return items[tab]?.keyName === 'equipment'
      ? equipmentFilterCount
      : items[tab]?.keyName === 'structure'
      ? structureFilterCount
      : items[tab]?.keyName === 'pipeline'
      ? pipelineFilterCount
      : tasksFilterCount;
  }, [
    tab,
    items,
    equipmentFilterCount,
    structureFilterCount,
    pipelineFilterCount,
    tasksFilterCount
  ]);

  const tabIsActive = useMemo(() => {
    return (
      isFilterActiveForEquipments ||
      isFilterActiveForStructure ||
      isFilterActiveForPipeline ||
      false
    );
  }, [isFilterActiveForEquipments, isFilterActiveForStructure, isFilterActiveForPipeline]);

  useEffect(() => {
    if (width <= 768) {
      const elements = document.getElementsByClassName('react-swipeable-view-container');

      for (let i = 0; i < elements.length; i++) {
        let element = elements[i];
        if (tasks?.length > 0) {
          if (tab === 1) element.style.transform = tabIsActive ? 'none' : 'translate(-100%, 0px)';
          if (tab === 2) element.style.transform = tabIsActive ? 'none' : 'translate(-200%, 0px)';
          if (tab === 3) element.style.transform = tabIsActive ? 'none' : 'translate(-300%, 0px)';
        } else {
          if (parseInt(tab) === 0)
            element.style.transform = tabIsActive ? 'none' : 'translate(0px, 0px)';
          if (tab === 1) element.style.transform = tabIsActive ? 'none' : 'translate(-100%, 0px)';
          if (tab === 2) element.style.transform = tabIsActive ? 'none' : 'translate(-200%, 0px)';
        }
      }
    }
  }, [tabIsActive, width]);

  useEffect(() => {
    dispatch?.(siteMapActions.resetMapState());
  }, [dispatch]);

  const siteNotFoundInBookmark = useMemo(() => {
    if (bookmarkName && !isLoading) {
      if (name && area) return false;
      else return true;
    }
    return false;
  }, [bookmarkName, isLoading, name, area]);

  // useEffect(() => {
  //   const googleMapOfAssetDetailPage = document?.getElementById('google-map-asset-page');
  //   const siteCardContainerBottom = document?.getElementById('site-card-container-bottom');
  //   const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;

  //   if (!isLoading && googleMapOfAssetDetailPage && siteCardOffsetHeight) {
  //     const heightOfNavbarAndTabsMenu = width >= 768 ? 94 + 32 + 16 : 86 + 32 + 16 + 84;
  //     const heightOfMap = heightOfNavbarAndTabsMenu + parseInt(siteCardOffsetHeight);
  //     googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
  //     googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
  //   }

  //   if (siteCardContainerBottom && selectedSiteIdForMapView.siteId && googleMapOfAssetDetailPage) {
  //     const siteCardContainerBottomSelectedSite =
  //       document?.getElementById(`site-card-container-bottom-${selectedSiteIdForMapView.siteIndex}`)
  //         ?.offsetHeight || 0;

  //     const currentScrollPosition =
  //       googleMapOfAssetDetailPage?.getAttribute('current-scroll-position') || 0;

  //     const heightOfNavbarAndTabsMenu =
  //       (width >= 768 ? 94 + 32 + 16 : 86 + 32 + 16 + 84) + siteCardContainerBottomSelectedSite;
  //     const heightOfMap =
  //       heightOfNavbarAndTabsMenu + parseInt(siteCardOffsetHeight) - currentScrollPosition;
  //     googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
  //     googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
  //   } else {
  //     if (googleMapOfAssetDetailPage) {
  //       const previousHeight = googleMapOfAssetDetailPage?.getAttribute('data-height') || 0;
  //       const currentScrollPosition =
  //         googleMapOfAssetDetailPage?.getAttribute('current-scroll-position') || 0;

  //       googleMapOfAssetDetailPage.style.height = `calc(100vh - ${
  //         previousHeight - currentScrollPosition
  //       }px)`;
  //     }
  //   }
  // }, [
  //   isLoading,
  //   siteCardRef?.current,
  //   width,
  //   document?.getElementById('google-map-asset-page'),
  //   document?.getElementById('site-card-container-bottom'),
  //   selectedSiteIdForMapView
  // ]);

  const lastScrollPosition = useRef(0);

  useEffect(() => {
    if (selectedSiteIdForMapView?.siteId) {
      dispatch?.(
        siteMapActions.setSelectedSiteIdOnMapView({
          siteId: null,
          siteIndex: -1
        })
      );
    }
  }, [tab, dispatch]);

  const allInspectionsList = useMemo(() => {
    return [
      ...(assetDetailsData?.allInspections || []),
      ...(assetDetailsData?.newInspectionList || [])
    ];
  }, [inspections, assetDetailsData]);

  const allTasksList = useMemo(() => {
    return [
      ...(assetDetailsData?.tasks || []),
      ...(assetDetailsData.newWorkOrderList || []),
      ...(assetDetailsData?.hseEvents || []),
      ...(assetDetailsData?.newWorkRequestList || []),
      ...(assetDetailsData?.allEditEquipmentAssets || [])
    ];
  }, [allTask, assetDetailsData]);

  const allWorkOrderList = useMemo(() => {
    return [...(allWorkOrders || []), ...(assetDetailsData?.newWorkOrderList || [])];
  }, [workOrder, assetDetailsData, allWorkOrders]);

  const allChecksList = useMemo(() => {
    return [...(allChecks || []), ...(assetDetailsData?.newChecksList || [])];
  }, [checks, assetDetailsData, allChecks]);

  useEffect(() => {
    if (!isLoading && !name && !status) {
      navigate('/error?assetNotFound=true');
    }
  }, [isLoading, name, status]);

  const onDeleteBookmark = async () => {
    try {
      if (!bookmarkId) {
        ToasterUtil.showError('Unable to delete bookmark: Bookmark ID is missing.');
        return;
      }

      await BookmarksService.deleteBookmarks({ bookmarkId: bookmarkId });

      dispatch?.(bookmarksActions.setBookmarkValidity(true));

      ToasterUtil.showSuccess('Bookmark deleted successfully');

      const params = new URLSearchParams(location.search);

      params.set('from', 'site');
      params.delete('bookmarkName');
      params.delete('bookmarkId');

      setDeleteBookmark(false);

      const newUrl = `${location.pathname}?${params.toString()}`;

      // Use navigate with replace
      navigate(newUrl, { replace: true });
    } catch (error) {
      ToasterUtil.showError('Failed to delete bookmark');
    }
  };

  const { moreInfoTabActive, isTasksTabActive } = useMemo(() => {
    const parseTab = parseInt(tab);
    return {
      moreInfoTabActive: items[parseTab]?.keyName === 'info' ? true : false,
      isTasksTabActive: items[parseTab]?.keyName === 'tasks' ? true : false
    };
  }, [items, tab]);

  // TODO: scroll logic start from here
  const swipeable = document.getElementById('react-swipeable-view-container') || null;
  const siteInfoTab = document.getElementById('site-info-tab') || null;
  const taskInfoTab = document.getElementById('tasks-info-tab') || null;

  useEffect(() => {
    // const currentScrollPosition = mainDivRef?.current?.scrollTop;
    // const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;
    // if (siteCardRef?.current && mainDivRef?.current) {
    //   const container =
    //     moreInfoTabActive && siteInfoTab
    //       ? siteInfoTab
    //       : isTasksTabActive && taskInfoTab
    //       ? taskInfoTab
    //       : null;

    //   if (container && (moreInfoTabActive || isTasksTabActive)) {
    //     const topHeaderHeight = width > 769 ? 94 : 86;
    //     const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
    //     const navigationTabsHeight = 33;
    //     const bottomFooterHeight = width > 769 ? 0 : 84;
    //     const totalHeight =
    //       topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;
    //     container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
    //   }

    if (swipeable) {
      if (moreInfoTabActive) {
        swipeable.style.background = '#ffffff';
      } else {
        swipeable.style.background = '';
      }
    }
  }, [
    // mainDivRef,
    // tab,
    moreInfoTabActive,
    swipeable
    // siteInfoTab,
    // taskInfoTab,
    // isTasksTabActive,
    // siteCardRef
  ]);

  const onScrollParentEvent = (event) => {
    const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;
    const currentScrollPosition = event.target.scrollTop || 0;

    // google map start

    const googleMapOfAssetDetailPage = document?.getElementById('google-map-asset-page');
    const siteCardContainerBottom = document?.getElementById('site-card-container-bottom');
    const siteCardContainerBottomSelectedSite =
      (siteCardContainerBottom &&
        selectedSiteIdForMapView.siteId &&
        siteCardContainerBottom.getAttribute('style') &&
        document?.getElementById(`site-card-container-bottom-${selectedSiteIdForMapView.siteIndex}`)
          ?.offsetHeight) ||
      0;
    const heightOfNavbarAndTabsMenu =
      width >= 768 ? 94 + 32 + 16 : 86 + 32 + 16 + 84 + siteCardContainerBottomSelectedSite;
    if (googleMapOfAssetDetailPage && items[tab]?.keyName?.toLowerCase()?.includes('map')) {
      if (currentScrollPosition > lastScrollPosition.current) {
        // check if user is scrolling up
        console.log('up');
        const heightOfMap =
          heightOfNavbarAndTabsMenu +
          parseInt(siteCardOffsetHeight) -
          parseInt(currentScrollPosition);
        googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
        googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
        googleMapOfAssetDetailPage?.setAttribute('current-scroll-position', currentScrollPosition);
      } else if (currentScrollPosition < lastScrollPosition.current) {
        console.log('down');
        // check if user is scrolling down
        const heightOfMap =
          heightOfNavbarAndTabsMenu +
          (parseInt(siteCardOffsetHeight) - parseInt(currentScrollPosition));
        googleMapOfAssetDetailPage.style.height = `calc(100vh - ${heightOfMap}px)`;
        googleMapOfAssetDetailPage?.setAttribute('data-height', heightOfMap);
        googleMapOfAssetDetailPage?.setAttribute('current-scroll-position', currentScrollPosition);
      }
    }
    // google map end

    if (siteCardRef?.current) {
      const topHeaderHeight = width > 769 ? 94 : 86;
      const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
      const navigationTabsHeight = 33;
      const bottomFooterHeight = width > 769 ? 0 : 84;
      const totalHeight =
        topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;

      const container =
        moreInfoTabActive && siteInfoTab
          ? siteInfoTab
          : isTasksTabActive && taskInfoTab
          ? taskInfoTab
          : null;
      if (container && (moreInfoTabActive || isTasksTabActive)) {
        container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
      }

      const containerIds = [
        'structure-listing-pages-container',
        'site-listing-pages-container',
        'pipeline-listing-pages-container',
        'equipment-listing-pages-container'
      ];
      const sortingBarHeight = width > 769 ? 69 : 53;

      containerIds.forEach((id) => {
        const container = document.getElementById(id);
        if (container) {
          // error
          container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition + 10}px)`;

          // const dataAttribute = `data-${id.replace(
          //   '-listing-pages-container',
          //   'PageHeightOnDetailsPage'
          // )}`;
          // const totalHeight = container.getAttribute(dataAttribute) || 0;
          // container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition + 10}px)`;
        }
      });
    }

    const tasksRecordsListsTabs = document?.querySelectorAll('#tasksRecordsListsTabs');
    for (let a = 0; a < tasksRecordsListsTabs.length; a++) {
      const totalHeight =
        tasksRecordsListsTabs[a]?.getAttribute('data-pageHeightOnDetailsPage') || 0;
      tasksRecordsListsTabs[a].style.height = `calc(100svh  - ${
        totalHeight - currentScrollPosition
      }px)`;
    }

    lastScrollPosition.current = currentScrollPosition;
  };
  // TODO: scroll logic end  here

  useEffect(() => {
    if (!isLoading && selectedTaskType) {
      if (selectedTaskType === 'check' && allChecks?.length) setChecks(true);
      else if (selectedTaskType === 'inspection' && allInspections.length) setInspections(true);
      else if (
        selectedTaskType === 'workOrder' &&
        (allWorkOrders.length ||
          allDoneWorkOrders?.length ||
          assetDetailsData?.newWorkOrderList?.length)
      )
        setWorkOrder(true);
      else if (selectedTaskType === 'workRequest' && newWorkRequestList?.length)
        setWorkRequest(true);
      else if (selectedTaskType === 'hseEvents' && hseEvents?.length) setHseEvent(true);
      else if (selectedTaskType === 'NewOrEditAsset' && allEditEquipmentAssets?.length)
        setNewOrEditAssets(true);
    }
  }, [
    selectedTaskType,
    isLoading,
    allChecks,
    allInspections,
    allWorkOrders,
    newWorkRequestList,
    hseEvents,
    allEditEquipmentAssets,
    allDoneWorkOrders,
    assetDetailsData?.newWorkOrderList?.length
  ]);

  return (
    <>
      {!isLoading && siteNotFoundInBookmark ? (
        <BookmarkErrorScreen />
      ) : (
        <PageContainer moreInfo={moreInfo}>
          <PageHeader
            title={pageHeaderTitle}
            {...(isFilterIconIsRequired && {
              setFilterActive: isFilterIconIsRequired
            })}
            disableBack={
              allTask ||
              checks ||
              inspections ||
              workOrder ||
              workRequest ||
              hseEvent ||
              newOrEditAssets
            }
            onClick={pageHeaderClickEvent}
            count={filtersCount}
            ref={assetDetailPageHeaderRef}
          />

          {isLoading ? (
            <LoadingRound className={style.loading} />
          ) : (
            <div>
              <div
                ref={mainDivRef}
                id="assetDetailPage"
                className={siteDetailsPageClassNames}
                style={{
                  position: 'relative',
                  paddingBottom: 0
                }}
                onScroll={onScrollParentEvent}
              >
                <SiteCardCOmponent
                  moreInfoTabActive={moreInfoTabActive}
                  showCard={width > 768 ? true : false}
                />
                {moreInfo ? (
                  <SiteDetailsMoreInfo moreInfoDataPointsMap={MoreInfoDataPointsMap} />
                ) : (
                  <div className="flex relative" id="details-page-tasks-records-container">
                    <div
                      style={{
                        overflow: 'hidden',
                        marginTop: '0px',
                        width: '100%'
                      }}
                      className={`${
                        moreInfo ? style.pageListingsWithMoreInfo : style.pageListings
                      } mobileMargin`}
                    >
                      {allTask ||
                      inspections ||
                      workOrder ||
                      workRequest ||
                      hseEvent ||
                      checks ||
                      newOrEditAssets ? (
                        <WorkOrder
                          tasks={
                            inspections
                              ? allInspectionsList
                              : allTask
                              ? allTasksList
                              : workOrder
                              ? allWorkOrderList
                              : workRequest
                              ? newWorkRequestList
                              : hseEvent
                              ? hseEvents
                              : checks
                              ? allChecksList
                              : newOrEditAssets
                              ? allEditEquipmentAssets
                              : []
                          }
                          workRequest={workRequest}
                          newOrEditAssets={newOrEditAssets}
                          hseEvent={hseEvent}
                          isFilterActiveHeader={isFilterActive}
                          setFilterActiveHeader={setFilterActive}
                          setSyncComplete={setSyncComplete}
                          selectTodoOrInProgressStatus={!_.isEmpty(bookmarkName) ? true : false}
                          onParentClickEvent={onParentClickEvent}
                          workOrderSelectedTab={siteDetailsPage?.workOrderTab || null}
                          workOrderSortTab={siteDetailsPage?.workOrderSortTab || null}
                          siteDetails
                          noDistanceTab
                          filterType={
                            allTask
                              ? 'all'
                              : inspections
                              ? 'inspection'
                              : workOrder
                              ? 'workOrder'
                              : workRequest
                              ? 'workRequest'
                              : hseEvent
                              ? 'hseEvents'
                              : checks
                              ? 'check'
                              : newOrEditAssets
                              ? 'newOrEditAssets'
                              : null
                          }
                          asset="sites"
                        />
                      ) : items?.length ? (
                        <div
                          className={`assetDetailNavNotFixed`}
                          ref={swipeAbleMenuRef}
                          id="assetDetailsNavigation"
                        >
                          <NewTabs
                            active={tab}
                            tabs={items}
                            setActive={(tab) => {
                              setTab(tab);
                            }}
                            className="mobileMargin"
                            swipeAbleNavbarWhiteBackground={moreInfoTabActive ? true : false}
                          />
                        </div>
                      ) : (
                        <div
                          className="no-result"
                          style={{ flex: 1, justifyContent: 'flex-start', gap: '20px' }}
                        >
                          <div>No results</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {openModal && (
                  <SiteBookmark
                    open={openModal}
                    setOpen={(value) => {
                      setOpenModal(value);
                    }}
                    placeholder="My favorite truck"
                    type="Site Details"
                    siteId={id || null}
                    editMode={bookmarkId}
                    bookmarkPrefillName={bookmarkName}
                    isBookmarkAtAGlance={isBookmarkAtAGlance}
                    onDelete={() => {
                      setDeleteBookmark(true);
                      setOpenModal(false);
                    }}
                  />
                )}
                {deleteBookmark && (
                  <DeleteModal
                    open={deleteBookmark}
                    setOpen={setDeleteBookmark}
                    handleDelete={onDeleteBookmark}
                    confirmDeletionBookmark
                    recordType="Bookmark"
                  />
                )}
              </div>
            </div>
          )}

          {[assetDetailsData]?.length > 0 &&
            coordinatesList?.allSiteCoordinatesList?.length > 0 &&
            width < 1025 && (
              <SiteCardMenuBottom
                selectedSiteIdForMapView={selectedSiteIdForMapView}
                siteChildrenList={[assetDetailsData]}
                openMapMenu={true}
                picklistsMap={picklistsMap}
                tab={tab}
              />
            )}
        </PageContainer>
      )}
    </>
  );
};

export default memo(AssetsDetails);
